(function () {
  const instances = new WeakMap();

  class DSImageHighlights {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-ds-ih-track]');
      this.prev = root.querySelector('[data-ds-ih-prev]');
      this.next = root.querySelector('[data-ds-ih-next]');
      this.thumb = root.querySelector('[data-ds-ih-thumb]');

      this.isDragging = false;
      this.didDrag = false;
      this.startX = 0;
      this.startScrollLeft = 0;

      this.onPrevClick = this.scrollPrev.bind(this);
      this.onNextClick = this.scrollNext.bind(this);
      this.onScroll = this.update.bind(this);
      this.onResize = this.update.bind(this);

      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerUp = this.handlePointerUp.bind(this);
      this.onClickCapture = this.handleClickCapture.bind(this);

      this.init();
    }

    init() {
      if (!this.track) return;

      if (this.prev) {
        this.prev.addEventListener('click', this.onPrevClick);
      }

      if (this.next) {
        this.next.addEventListener('click', this.onNextClick);
      }

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      this.track.addEventListener('pointerdown', this.onPointerDown);
      this.track.addEventListener('pointermove', this.onPointerMove);
      this.track.addEventListener('pointerup', this.onPointerUp);
      this.track.addEventListener('pointercancel', this.onPointerUp);
      this.track.addEventListener('mouseleave', this.onPointerUp);

      this.root.addEventListener('click', this.onClickCapture, true);

      this.update();
    }

    getScrollAmount() {
      return Math.max(240, this.track.clientWidth * 0.75);
    }

    scrollPrev(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.track.scrollBy({
        left: this.getScrollAmount() * -1,
        behavior: 'smooth'
      });
    }

    scrollNext(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      this.track.scrollBy({
        left: this.getScrollAmount(),
        behavior: 'smooth'
      });
    }

    handlePointerDown(event) {
      if (event.button !== undefined && event.button !== 0) return;

      this.isDragging = true;
      this.didDrag = false;
      this.startX = event.clientX;
      this.startScrollLeft = this.track.scrollLeft;

      this.track.classList.add('is-dragging');

      try {
        this.track.setPointerCapture(event.pointerId);
      } catch (error) {}
    }

    handlePointerMove(event) {
      if (!this.isDragging) return;

      const deltaX = event.clientX - this.startX;

      if (Math.abs(deltaX) > 6) {
        this.didDrag = true;
      }

      this.track.scrollLeft = this.startScrollLeft - deltaX;
    }

    handlePointerUp(event) {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.track.classList.remove('is-dragging');

      try {
        this.track.releasePointerCapture(event.pointerId);
      } catch (error) {}

      this.update();
    }

    handleClickCapture(event) {
      if (!this.didDrag) return;

      event.preventDefault();
      event.stopPropagation();

      this.didDrag = false;
    }

    update() {
      if (!this.track) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const current = this.track.scrollLeft;
      const hasOverflow = maxScroll > 4;

      if (this.prev) {
        this.prev.disabled = !hasOverflow || current <= 2;
      }

      if (this.next) {
        this.next.disabled = !hasOverflow || current >= maxScroll - 2;
      }

      if (this.thumb) {
        if (!hasOverflow) {
          this.thumb.style.width = '100%';
          this.thumb.style.transform = 'translateX(0px)';
        } else {
          const visibleRatio = this.track.clientWidth / this.track.scrollWidth;
          const thumbWidthPercent = Math.max(16, visibleRatio * 100);
          const travelPercent = 100 - thumbWidthPercent;
          const progress = current / maxScroll;

          this.thumb.style.width = thumbWidthPercent + '%';
          this.thumb.style.transform = 'translateX(' + progress * travelPercent + '%)';
        }
      }

      this.updateInsufficientClass(hasOverflow);
    }

    updateInsufficientClass(hasOverflow) {
      if (!this.track) return;

      if (hasOverflow) {
        this.track.classList.remove('is-insufficient');
      } else {
        this.track.classList.add('is-insufficient');
      }
    }

    destroy() {
      if (this.prev) {
        this.prev.removeEventListener('click', this.onPrevClick);
      }

      if (this.next) {
        this.next.removeEventListener('click', this.onNextClick);
      }

      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('pointermove', this.onPointerMove);
        this.track.removeEventListener('pointerup', this.onPointerUp);
        this.track.removeEventListener('pointercancel', this.onPointerUp);
        this.track.removeEventListener('mouseleave', this.onPointerUp);
      }

      window.removeEventListener('resize', this.onResize);
      this.root.removeEventListener('click', this.onClickCapture, true);
    }
  }

  function initImageHighlights(scope) {
    const root = scope || document;

    root.querySelectorAll('[data-ds-image-highlights]').forEach((section) => {
      if (!instances.has(section)) {
        instances.set(section, new DSImageHighlights(section));
      }
    });
  }

  function destroyImageHighlights(scope) {
    const root = scope || document;

    root.querySelectorAll('[data-ds-image-highlights]').forEach((section) => {
      const instance = instances.get(section);

      if (instance) {
        instance.destroy();
        instances.delete(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initImageHighlights(document);
    });
  } else {
    initImageHighlights(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initImageHighlights(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyImageHighlights(event.target);
  });
})();